import React, { useEffect, useState } from "react";
import { Image } from "antd";
import { PictureOutlined } from "@ant-design/icons";

import "./ChecklistPhotoPreview.css";

/**
 * Thumbnail of the first photo a worker attached to a checklist item, with
 * a "+N" badge when there are more. Clicking it opens antd's full-screen
 * viewer (zoom, rotate, next/previous) at the first photo. Shows a muted
 * dash when there are no photos.
 */
export default function ChecklistPhotoPreview({ urls, itemTitle }) {
  const [open, setOpen] = useState(false);
  const [thumbFailed, setThumbFailed] = useState(false);

  // The viewer's own Escape handling is unreliable here (ignored while it
  // animates open, swallowed once its arrows are used), so close on Escape
  // ourselves. Capture phase: the viewer stops the event before it bubbles.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [open]);

  if (!urls || urls.length === 0) {
    return (
      <span className="checklist-photo-none" aria-label="No photos">
        —
      </span>
    );
  }

  const label = urls.length === 1 ? "View photo" : `View ${urls.length} photos`;
  const extra = urls.length - 1;

  return (
    <>
      <button
        type="button"
        className="checklist-photo-thumb"
        onClick={() => setOpen(true)}
        aria-label={itemTitle ? `${label}: ${itemTitle}` : label}
        title={label}
      >
        {thumbFailed ? (
          <PictureOutlined className="checklist-photo-thumb-fallback" aria-hidden="true" />
        ) : (
          <img src={urls[0]} alt="" loading="lazy" onError={() => setThumbFailed(true)} />
        )}
        {extra > 0 && (
          <span className="checklist-photo-thumb-more" aria-hidden="true">
            +{extra}
          </span>
        )}
      </button>
      {/* The viewer only; its own thumbnails are never shown. */}
      <div className="checklist-photo-hidden">
        <Image.PreviewGroup preview={{ visible: open, onVisibleChange: (visible) => setOpen(visible) }}>
          {urls.map((url, index) => (
            <Image key={url} src={url} alt={`${itemTitle || "Checklist item"}: photo ${index + 1}`} />
          ))}
        </Image.PreviewGroup>
      </div>
    </>
  );
}
